// src/pages/components/WhatsAppPro.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import api from '../../services/api';

// ============================================================================
// ICONS
// ============================================================================
const Icons = {
  Search: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  Send: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  ),
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 16h6v6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 8h-6V2" />
    </svg>
  ),
  Menu: () => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  ),
  Back: () => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  ),
  Phone: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.36 1.78.7 2.61a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.47-1.27a2 2 0 0 1 2.11-.45c.83.34 1.71.58 2.61.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Check: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  CheckAll: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#34b7f1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m18 7-8 8-4-4" />
      <path d="m22 7-8 8" />
    </svg>
  ),
  Clock: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  Alert: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16h.01" />
    </svg>
  ),
  Spark: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z" />
      <path d="M19 16l.7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7L19 16z" />
    </svg>
  ),
  Building: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V5a2 2 0 0 1 2-2h7v18" />
      <path d="M14 8h3a2 2 0 0 1 2 2v11" />
      <path d="M8 7h2" />
      <path d="M8 11h2" />
      <path d="M8 15h2" />
    </svg>
  ),
  Message: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  )
};

// ============================================================================
// HELPERS
// ============================================================================
function safeParseUser() {
  const keys = ['lumixUser', 'vizumUser', 'appUser', 'user', 'authUser'];

  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw) || {};
    } catch {
      // ignora
    }
  }

  return {};
}

function normalizarId(valor) {
  if (!valor) return '';

  if (typeof valor === 'object') {
    return String(valor._id || valor.id || valor.toString?.() || '');
  }

  return String(valor);
}

function isMongoId(valor) {
  return /^[0-9a-fA-F]{24}$/.test(String(valor || ''));
}

function getEmpresaIdFromUser(user = {}) {
  return normalizarId(
    user?.empresaId?._id ||
      user?.empresaId ||
      user?.empresa?._id ||
      user?.empresa?.id ||
      user?.empresa ||
      ''
  );
}

function resolverArrayApi(data, keys = []) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function limparTextoBusca(valor = '') {
  return String(valor || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function normalizarNumero(numero = '') {
  let clean = String(numero || '').replace(/\D/g, '');

  if (!clean) return '';

  if (!clean.startsWith('55') && clean.length <= 11) {
    clean = `55${clean}`;
  }

  return clean;
}

function firstName(nome = '') {
  return String(nome || '').trim().split(' ')[0] || 'Cliente';
}

function initials(nome = '') {
  const parts = String(nome || '').trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return 'CL';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function formatTime(date) {
  if (!date) return '';

  try {
    return new Date(date).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}

function formatDateShort(date) {
  if (!date) return '--/--';

  try {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    });
  } catch {
    return '--/--';
  }
}

function montarErroApi(error) {
  if (!navigator.onLine) return 'Você está sem internet. Verifique sua conexão.';

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Erro ao comunicar com a API.'
  );
}

function getSocketUrl() {
  const base = api.defaults.baseURL || window.location.origin;

  try {
    const url = new URL(base, window.location.origin);
    return `${url.protocol}//${url.host}`;
  } catch {
    return window.location.origin;
  }
}

function getCodigoLoja(item = {}) {
  return String(
    item?.codigoLoja ||
      item?.loja ||
      item?.lojaCodigo ||
      item?.empresa?.codigoLoja ||
      item?.unidade?.codigoLoja ||
      item?.raw?.codigoLoja ||
      item?.raw?.loja ||
      ''
  ).trim();
}

function getClienteEmpresaId(cliente = {}) {
  return normalizarId(
    cliente?.empresaId?._id ||
      cliente?.empresaId ||
      cliente?.unidadeId ||
      cliente?.unidade?._id ||
      cliente?.unidade?.id ||
      cliente?.empresaUnidadeId ||
      ''
  );
}

function getClienteMatrizId(cliente = {}) {
  return normalizarId(
    cliente?.empresaMatrizId ||
      cliente?.empresaOrigemId ||
      cliente?.matrizId ||
      getClienteEmpresaId(cliente)
  );
}

function getClienteEmpresaNome(cliente = {}) {
  return (
    cliente?.empresaNome ||
    cliente?.empresa?.nome ||
    cliente?.unidade?.nome ||
    cliente?.unidadeNome ||
    cliente?.loja ||
    'Empresa'
  );
}

function getClienteNome(cliente = {}) {
  return cliente?.cliente?.nome || cliente?.nome || 'Cliente sem nome';
}

function getClienteTelefone(cliente = {}) {
  return normalizarNumero(cliente?.whatsappClean || cliente?.whatsapp || cliente?.cliente?.telefone || '');
}

function normalizarStatusBackend(status, connected = false) {
  const s = String(status || '').toUpperCase();

  if (connected === true || s === 'CONNECTED' || s === 'INCHAT' || s === 'CHATSAVAILABLE') return 'CONNECTED';
  if (s === 'QRCODE' || s === 'QR_CODE' || s === 'QR') return 'QRCODE';
  if (s === 'INITIALIZING' || s === 'LOADING' || s === 'OPENING') return 'INITIALIZING';
  if (s === 'DISCONNECTING') return 'DISCONNECTING';
  if (s === 'ERROR') return 'ERROR';

  return 'DISCONNECTED';
}

function normalizarCanalWhatsapp(canal = {}, empresaIdFallback = '') {
  const runtime = canal.runtime || {};

  const id =
    normalizarId(canal._id) ||
    normalizarId(canal.id) ||
    normalizarId(canal.canalWhatsappId) ||
    normalizarId(canal.canalId);

  const rawStatus = runtime.status || canal.status || canal.state || '';
  const connected = runtime.connected === true || canal.connected === true || rawStatus === 'CONNECTED';
  const status = normalizarStatusBackend(rawStatus, connected);

  const empresaId = normalizarId(
    canal.empresaId?._id ||
      canal.empresaId ||
      canal.empresa?._id ||
      canal.empresa?.id ||
      canal.empresaOperacionalId ||
      empresaIdFallback
  );

  const unidadeId = normalizarId(
    canal.unidadeId?._id ||
      canal.unidadeId ||
      canal.unidade?._id ||
      canal.unidade?.id ||
      canal.unidadeOperacionalId ||
      empresaId
  );

  const empresaMatrizId = normalizarId(
    canal.empresaMatrizId?._id ||
      canal.empresaMatrizId ||
      canal.empresaOrigemId?._id ||
      canal.empresaOrigemId ||
      canal.matrizId ||
      empresaIdFallback ||
      empresaId
  );

  return {
    raw: canal,
    id,
    empresaId,
    unidadeId,
    empresaMatrizId,
    empresaOrigemId: normalizarId(canal.empresaOrigemId || empresaMatrizId),
    grupoEmpresaId: normalizarId(canal.grupoEmpresaId || canal.checkoutSessionId || empresaMatrizId),
    checkoutSessionId: normalizarId(canal.checkoutSessionId || canal.assinatura?.checkoutSessionId),
    nome:
      canal.nome ||
      canal.label ||
      canal.apelido ||
      canal.sessionName ||
      canal.numero ||
      `Canal ${id ? id.slice(-6) : ''}`,
    empresaNome:
      canal.empresaId?.nome ||
      canal.empresa?.nome ||
      canal.nomeEmpresa ||
      canal.unidadeId?.nome ||
      canal.unidadeNome ||
      'Empresa',
    unidadeNome:
      canal.unidadeId?.unidade ||
      canal.unidadeId?.nome ||
      canal.empresaId?.unidade ||
      canal.empresaId?.nome ||
      canal.unidade?.nome ||
      canal.unidadeNome ||
      canal.nomeUnidade ||
      canal.loja ||
      canal.codigoLoja ||
      canal.unidade ||
      'Unidade principal',
    codigoLoja: canal.codigoLoja || canal.loja || canal.unidadeId?.codigoLoja || canal.empresaId?.codigoLoja || canal.unidade?.codigoLoja || '',
    numero: canal.numero || canal.telefone || canal.whatsapp || '',
    principal: canal.principal === true || canal.isDefault === true,
    sessionName: runtime.sessionName || canal.sessionName || '',
    status,
    connected: status === 'CONNECTED',
    lastStatusAt: runtime.lastStatusAt || canal.lastStatusAt || canal.ultimoStatusEm || null
  };
}

function normalizarEmpresaGrupo(empresa = {}) {
  const id = normalizarId(empresa._id || empresa.id || empresa.empresaId);

  if (!id) return null;

  return {
    id,
    nome: empresa.nome || empresa.unidade || empresa.nomeFantasia || empresa.codigoLoja || `Empresa ${id.slice(-4)}`,
    codigoLoja: getCodigoLoja(empresa),
    unidade: empresa.unidade || '',
    empresaMatrizId: normalizarId(empresa.empresaMatrizId || empresa.empresaOrigemId || empresa.matrizId || id),
    ativo: empresa.ativo !== false
  };
}

function normalizarOrdemServico(os = {}) {
  const id = normalizarId(os._id || os.id);
  const whatsappClean = getClienteTelefone(os);

  return {
    ...os,
    id,
    whatsappClean,
    nomeBusca: getClienteNome(os),
    codigoLoja:
      os.codigoLoja ||
      os.loja ||
      os.empresa?.codigoLoja ||
      os.unidade?.codigoLoja ||
      os.lojaCodigo ||
      os.codigo ||
      '',
    unidadeId: normalizarId(os.unidadeId || os.unidade?._id || os.unidade?.id || os.empresaUnidadeId || os.empresaId),
    empresaId: normalizarId(os.empresaId || os.unidadeId || os.unidade?._id || os.unidade?.id || os.empresaUnidadeId),
    empresaMatrizId: normalizarId(os.empresaMatrizId || os.empresaOrigemId || os.matrizId || os.empresaId),
    empresaNome: os.empresa?.nome || os.unidade?.nome || os.unidadeNome || os.loja || os.empresa || '',
    canalWhatsappId: normalizarId(os.canalWhatsappId || os.whatsappCanalId || os.unidadeWhatsappId)
  };
}

function escolherCanalParaCliente(cliente, canais = [], canalSelecionado = null) {
  const canaisOnline = canais.filter((canal) => canal?.connected && isMongoId(canal.id));

  if (!canaisOnline.length) return null;

  if (canalSelecionado?.id && canalSelecionado.connected && isMongoId(canalSelecionado.id)) {
    return canalSelecionado;
  }

  const clienteCanalId = normalizarId(cliente?.canalWhatsappId);
  const clienteUnidadeId = normalizarId(cliente?.unidadeId || cliente?.unidade?._id || cliente?.unidade?.id || cliente?.empresaUnidadeId);
  const clienteEmpresaId = getClienteEmpresaId(cliente);
  const clienteMatrizId = getClienteMatrizId(cliente);
  const codigoLoja = limparTextoBusca(getCodigoLoja(cliente));

  if (clienteCanalId) {
    const canal = canaisOnline.find((item) => item.id === clienteCanalId);
    if (canal) return canal;
  }

  if (clienteUnidadeId) {
    const canal = canaisOnline.find((item) => String(item.unidadeId) === String(clienteUnidadeId));
    if (canal) return canal;
  }

  if (clienteEmpresaId) {
    const canal = canaisOnline.find((item) => String(item.empresaId) === String(clienteEmpresaId));
    if (canal) return canal;
  }

  if (codigoLoja) {
    const canal = canaisOnline.find((item) => limparTextoBusca(item.codigoLoja || item.unidadeNome || item.nome) === codigoLoja);
    if (canal) return canal;
  }

  if (clienteMatrizId) {
    const canal = canaisOnline.find((item) => String(item.empresaMatrizId) === String(clienteMatrizId));
    if (canal) return canal;
  }

  return canaisOnline.find((canal) => canal.principal) || canaisOnline[0] || null;
}

function extrairOsIdDoPayloadMensagem(data = {}) {
  const referenceContext =
    data.context?.referenceContext ||
    data.referenceContext ||
    data.context ||
    null;

  return normalizarId(
    data.osId ||
      data.referenceId ||
      data.context?.osId ||
      data.context?.referenceId ||
      data.context?.referenceContext?.osId ||
      data.context?.referenceContext?.ordemServicoId ||
      data.context?.referenceContext?.referenceId ||
      data.referenceContext?.osId ||
      data.referenceContext?.ordemServicoId ||
      data.referenceContext?.referenceId ||
      (typeof referenceContext === 'string' ? referenceContext : '')
  );
}

function buildMessageKey(msg = {}) {
  return [
    msg.serializedId || msg.id || '',
    msg.sender || '',
    msg.text || msg.message || '',
    msg.rawDate || msg.sentAt || ''
  ].join('|');
}

function getMessageStableKey(msg = {}) {
  const serializedId = String(msg.serializedId || '').trim();
  if (serializedId) return `serialized:${serializedId}`;

  const id = String(msg.id || '').trim();

  /*
    IDs temporários começam com tmp_ e IDs aleatórios podem mudar entre socket/API.
    Então só usamos id como chave forte quando ele parece ser persistente.
  */
  if (id && !id.startsWith('tmp_') && !id.includes('Math.random')) {
    return `id:${id}`;
  }

  const sender = String(msg.sender || '').trim();
  const text = String(msg.text || msg.message || msg.body || '').trim();
  const canal = String(msg.canalWhatsappId || '').trim();

  let dateBucket = '';

  try {
    const time = new Date(msg.rawDate || msg.sentAt || msg.createdAt || 0).getTime();

    if (Number.isFinite(time) && time > 0) {
      /*
        Agrupa por janela de 2 segundos para evitar duplicar a mesma mensagem
        quando socket e API chegam com diferença pequena de timestamp.
      */
      dateBucket = String(Math.round(time / 2000));
    }
  } catch {
    dateBucket = '';
  }

  return `soft:${sender}:${canal}:${text}:${dateBucket}`;
}

function mergeMessageLists(localMessages = [], apiMessages = []) {
  const map = new Map();

  /*
    Primeiro entra o que já está na tela.
    Assim uma resposta recebida via WebSocket não desaparece se a API ainda
    não retornou ela no histórico.
  */
  localMessages.forEach((message) => {
    const key = getMessageStableKey(message);
    map.set(key, message);
  });

  /*
    Depois entram os dados da API, atualizando mensagens equivalentes.
    Se a API trouxer versão persistida, ela substitui/complete a local.
  */
  apiMessages.forEach((message) => {
    const key = getMessageStableKey(message);
    const current = map.get(key);

    map.set(key, {
      ...(current || {}),
      ...message,
      status: message.status || current?.status || 'sent',
      success: message.success !== false && current?.success !== false
    });
  });

  return Array.from(map.values()).sort(
    (a, b) => new Date(a.rawDate || a.sentAt || 0) - new Date(b.rawDate || b.sentAt || 0)
  );
}

function replaceTemplateVars(template, client) {
  const nome = getClienteNome(client);
  const primeiroNome = firstName(nome);

  const vars = {
    '{{nome}}': primeiroNome,
    '{{cliente}}': nome,
    '{{os}}': client?.numeroOS || client?.produto?.numeroCV || 'S/N',
    '{{loja}}': getCodigoLoja(client) || 'loja',
    '{{status}}': client?.status || '',
    '{{vendedor}}': client?.vendedor?.nome || client?.vendedor || ''
  };

  let text = String(template || '');

  Object.entries(vars).forEach(([key, value]) => {
    text = text.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), value);
  });

  return text;
}

function groupMessagesByDate(messages = []) {
  const groups = {};
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  messages.forEach((msg) => {
    const d = new Date(msg.rawDate || msg.sentAt || msg.createdAt || Date.now());

    let key = d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    if (d.toDateString() === today.toDateString()) key = 'Hoje';
    if (d.toDateString() === yesterday.toDateString()) key = 'Ontem';

    if (!groups[key]) groups[key] = [];
    groups[key].push(msg);
  });

  return groups;
}

function abrirTelaEmpresasCanal(canal = null, empresaId = '') {
  const params = new URLSearchParams();
  params.set('abrirCanal', '1');

  if (canal?.id) params.set('canalWhatsappId', canal.id);
  if (canal?.empresaId || empresaId) params.set('empresaId', canal?.empresaId || empresaId);
  if (canal?.unidadeId) params.set('unidadeId', canal.unidadeId);
  if (canal?.empresaMatrizId) params.set('empresaMatrizId', canal.empresaMatrizId);

  window.location.href = `/empresas?${params.toString()}`;
}

function getMessageFromApi(m = {}) {
  return {
    id: m.id || m.serializedId || `${m.sender || 'msg'}-${m.rawDate || m.sentAt || Date.now()}-${Math.random()}`,
    serializedId: m.serializedId || m.id || '',
    text: m.text || m.message || m.body || '',
    sender: m.sender || 'client',
    time: m.time || formatTime(m.rawDate || m.sentAt || m.createdAt),
    rawDate: m.rawDate || m.sentAt || m.createdAt || new Date().toISOString(),
    type: m.type || 'chat',
    status: m.status || 'sent',
    success: m.success !== false,
    canalWhatsappId: normalizarId(m.canalWhatsappId || m.channelId || m.context?.canalWhatsappId)
  };
}

function getIdentificadorRemetente(data = {}) {
  return String(
    data.from ||
      data.whatsappId ||
      data.number ||
      data.numero ||
      data.telefone ||
      data.senderNumber ||
      data.remoteJid ||
      data.chatId ||
      ''
  )
    .replace('@c.us', '')
    .replace('@lid', '')
    .replace('@s.whatsapp.net', '')
    .trim();
}

function getWhatsappIdRemetente(data = {}) {
  const raw = String(
    data.whatsappId ||
      data.from ||
      data.remoteJid ||
      data.chatId ||
      data.number ||
      data.numero ||
      ''
  ).trim();

  if (!raw) return '';

  if (raw.includes('@')) return raw;

  const onlyDigits = raw.replace(/\D/g, '');

  if (!onlyDigits) return '';

  if (onlyDigits.length > 13 && !onlyDigits.startsWith('55')) {
    return `${onlyDigits}@lid`;
  }

  return `${onlyDigits}@c.us`;
}

function getUnlinkedConversationId(data = {}) {
  const canalId = normalizarId(data.canalWhatsappId || data.channelId || data.canalId) || 'sem-canal';
  const remetente = getIdentificadorRemetente(data) || normalizarId(data.context?.dbId) || String(Date.now());

  return `semvinculo_${canalId}_${remetente}`;
}

function montarClienteSemVinculo(data = {}, canal = null) {
  const id = getUnlinkedConversationId(data);
  const remetente = getIdentificadorRemetente(data);
  const whatsappId = getWhatsappIdRemetente(data);
  const canalId = normalizarId(data.canalWhatsappId || data.channelId || data.canalId);

  return {
    id,
    isSemVinculo: true,
    nomeBusca: remetente ? `Contato ${remetente.slice(-8)}` : 'Contato sem vínculo',
    cliente: {
      nome: remetente ? `Contato ${remetente.slice(-8)}` : 'Contato sem vínculo',
      telefone: remetente
    },
    whatsapp: remetente,
    whatsappId,
    remoteJid: whatsappId,
    whatsappClean: whatsappId.includes('@lid') ? '' : normalizarNumero(remetente),
    codigoLoja: canal?.codigoLoja || data.codigoLoja || data.loja || 'sem-vinculo',
    loja: canal?.codigoLoja || data.codigoLoja || data.loja || 'sem-vinculo',
    status: 'Sem vínculo',
    statusmsg: 'Nova mensagem',
    empresaId: normalizarId(data.empresaId || canal?.empresaId),
    unidadeId: normalizarId(data.unidadeId || canal?.unidadeId),
    empresaMatrizId: normalizarId(data.empresaMatrizId || canal?.empresaMatrizId),
    empresaNome: canal?.empresaNome || 'WhatsApp',
    canalWhatsappId: canalId,
    numeroOS: 'Sem O.S.',
    createdAt: data.rawDate || data.sentAt || new Date().toISOString(),
    updatedAt: data.rawDate || data.sentAt || new Date().toISOString()
  };
}

const QUICK_TEMPLATES = [
  {
    id: 'saudacao',
    titulo: 'Saudação',
    texto: 'Olá, *{{nome}}*! Tudo bem? Aqui é da ótica. Como posso te ajudar hoje?'
  },
  {
    id: 'oculos-pronto',
    titulo: 'Óculos pronto',
    texto: 'Olá, *{{nome}}*! Seus óculos da OS *#{{os}}* já estão prontos aqui na loja. Pode vir retirar quando ficar melhor pra você. 👓'
  },
  {
    id: 'pos-venda',
    titulo: 'Pós-venda',
    texto: 'Oi, *{{nome}}*! Tudo bem? Passando para saber como está sua adaptação com os óculos. Qualquer ajuste, limpeza ou aperto, é só passar aqui na loja. É cortesia. 😊'
  },
  {
    id: 'ajuste',
    titulo: 'Ajuste grátis',
    texto: '*{{nome}}*, se a armação estiver apertando, escorregando ou incomodando, pode passar aqui na loja. Fazemos o ajuste na hora e sem custo. 👓'
  },
  {
    id: 'avaliacao',
    titulo: 'Avaliação',
    texto: 'Oi, *{{nome}}*! Sua opinião é muito importante pra gente. Se gostou do atendimento, poderia deixar uma avaliação rápida? Isso ajuda muito nossa ótica. ⭐'
  }
];

function ContactSkeletonList() {
  return (
    <div className="crm-skeleton-list">
      {Array.from({ length: 8 }).map((_, index) => (
        <div className="crm-skeleton-contact" key={index}>
          <span />
          <div>
            <strong />
            <small />
            <em />
          </div>
        </div>
      ))}
    </div>
  );
}

function MessageStatus({ message }) {
  if (message.status === 'pending') return <Icons.Clock />;
  if (message.status === 'failed' || message.success === false) return <Icons.Alert />;
  return <Icons.CheckAll />;
}

// ============================================================================
// COMPONENT
// ============================================================================
export default function WhatsAppPro({
  empresaIdAtual: empresaIdProp = '',
  canaisWhatsapp: canaisWhatsappProp = [],
  canalPrincipal: canalPrincipalProp = null
}) {
  const user = useMemo(() => safeParseUser(), []);
  const empresaIdAtual = useMemo(() => empresaIdProp || getEmpresaIdFromUser(user), [empresaIdProp, user]);
  const socketUrl = useMemo(() => getSocketUrl(), []);

  const [clientes, setClientes] = useState([]);
  const [historico, setHistorico] = useState({});
  const [selectedId, setSelectedId] = useState('');
  const [typedMessage, setTypedMessage] = useState('');
  const [search, setSearch] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('Todos');
  const [lojaFiltro, setLojaFiltro] = useState('Todas');
  const [canalFiltro, setCanalFiltro] = useState('todos');
  const [selectedCanalId, setSelectedCanalId] = useState('auto');
  const [canais, setCanais] = useState([]);
  const [empresasGrupo, setEmpresasGrupo] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingCanais, setLoadingCanais] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showMobileList, setShowMobileList] = useState(true);
  const [unreadByOs, setUnreadByOs] = useState({});
  const [mensagensSemVinculo, setMensagensSemVinculo] = useState([]);
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [vinculoSearch, setVinculoSearch] = useState('');
  const [vinculandoId, setVinculandoId] = useState('');
  const [planoCanais, setPlanoCanais] = useState({
    planoNome: 'Plano atual',
    limiteCanais: 1,
    total: 0,
    disponiveis: 1,
    podeCriar: true
  });

  const socketRef = useRef(null);
  const chatEndRef = useRef(null);
  const receivedKeysRef = useRef(new Set());
  const clientesRef = useRef([]);
  const canaisRef = useRef([]);
  const selectedIdRef = useRef('');
  const selectedCanalIdRef = useRef('auto');

  useEffect(() => {
    clientesRef.current = clientes;
  }, [clientes]);

  useEffect(() => {
    canaisRef.current = canais;
  }, [canais]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    selectedCanalIdRef.current = selectedCanalId;
  }, [selectedCanalId]);

  const selectedClient = useMemo(() => {
    return clientes.find((cliente) => cliente.id === selectedId) || null;
  }, [clientes, selectedId]);

  const selectedCanal = useMemo(() => {
    if (!selectedCanalId || selectedCanalId === 'auto') return null;
    return canais.find((canal) => canal.id === selectedCanalId) || null;
  }, [canais, selectedCanalId]);

  const canalPrincipalLocal = useMemo(() => {
    return (
      canalPrincipalProp ||
      canais.find((canal) => canal.principal && canal.connected) ||
      canais.find((canal) => canal.connected) ||
      canais.find((canal) => canal.principal) ||
      canais[0] ||
      null
    );
  }, [canais, canalPrincipalProp]);

  const canalPreview = useMemo(() => {
    if (!selectedClient) return canalPrincipalLocal;

    return escolherCanalParaCliente(
      selectedClient,
      canais,
      selectedCanalId === 'auto' ? null : selectedCanal
    ) || canalPrincipalLocal;
  }, [selectedClient, canais, selectedCanalId, selectedCanal, canalPrincipalLocal]);

  const currentChat = useMemo(() => historico[selectedId] || [], [historico, selectedId]);
  const groupedChat = useMemo(() => groupMessagesByDate(currentChat), [currentChat]);

  const canaisOnline = useMemo(() => canais.filter((canal) => canal.connected).length, [canais]);
  const canaisResumo = useMemo(() => canais.slice(0, 5), [canais]);

  const statusOptions = useMemo(() => {
    const set = new Set(['Todos']);

    clientes.forEach((cliente) => {
      const status = String(cliente.status || '').trim();
      if (status) set.add(status);
    });

    return Array.from(set);
  }, [clientes]);

  const lojaOptions = useMemo(() => {
    const set = new Set();

    empresasGrupo.forEach((empresa) => {
      const codigo = getCodigoLoja(empresa);
      if (codigo) set.add(codigo);
    });

    canais.forEach((canal) => {
      const codigo = getCodigoLoja(canal);
      if (codigo) set.add(codigo);
    });

    clientes.forEach((cliente) => {
      const codigo = getCodigoLoja(cliente);
      if (codigo) set.add(codigo);
    });

    return ['Todas', ...Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b, 'pt-BR'))];
  }, [clientes, canais, empresasGrupo]);

  const filteredClients = useMemo(() => {
    const termo = limparTextoBusca(search);
    const termoNumero = String(search || '').replace(/\D/g, '');

    return clientes
      .filter((cliente) => {
        const nome = limparTextoBusca(getClienteNome(cliente));
        const numeroOS = String(cliente.numeroOS || cliente.produto?.numeroCV || '').toLowerCase();
        const telefone = getClienteTelefone(cliente);

        const matchSearch =
          !termo ||
          nome.includes(termo) ||
          numeroOS.includes(termo) ||
          (termoNumero && telefone.includes(termoNumero));

        const matchStatus =
          statusFiltro === 'Todos' ||
          String(cliente.status || '').toLowerCase() === String(statusFiltro).toLowerCase();

        const matchLoja =
          lojaFiltro === 'Todas' ||
          limparTextoBusca(getCodigoLoja(cliente)) === limparTextoBusca(lojaFiltro);

        const matchCanal = (() => {
          if (canalFiltro === 'todos') return true;

          const canal = canais.find((item) => item.id === canalFiltro);
          if (!canal) return true;

          return escolherCanalParaCliente(cliente, [canal], null)?.id === canalFiltro;
        })();

        return matchSearch && matchStatus && matchLoja && matchCanal;
      })
      .sort((a, b) => {
        const unreadA = unreadByOs[a.id] || 0;
        const unreadB = unreadByOs[b.id] || 0;

        if (unreadA !== unreadB) return unreadB - unreadA;

        const chatA = historico[a.id] || [];
        const chatB = historico[b.id] || [];
        const dateA = chatA[chatA.length - 1]?.rawDate || a.updatedAt || a.createdAt || a.dataCompra || '';
        const dateB = chatB[chatB.length - 1]?.rawDate || b.updatedAt || b.createdAt || b.dataCompra || '';

        return new Date(dateB || 0) - new Date(dateA || 0);
      });
  }, [clientes, search, statusFiltro, lojaFiltro, canalFiltro, canais, historico, unreadByOs]);

  const osVinculoCandidates = useMemo(() => {
    const termo = limparTextoBusca(vinculoSearch);
    const termoNumero = String(vinculoSearch || '').replace(/\D/g, '');

    return clientes
      .filter((cliente) => !cliente.isSemVinculo)
      .filter((cliente) => {
        if (!termo && !termoNumero) return true;

        const nome = limparTextoBusca(getClienteNome(cliente));
        const os = String(cliente.numeroOS || cliente.produto?.numeroCV || '').toLowerCase();
        const loja = limparTextoBusca(getCodigoLoja(cliente));
        const telefone = getClienteTelefone(cliente);

        return (
          nome.includes(termo) ||
          os.includes(termo) ||
          loja.includes(termo) ||
          (termoNumero && telefone.includes(termoNumero))
        );
      })
      .slice(0, 80);
  }, [clientes, vinculoSearch]);

  const stats = useMemo(() => {
    const conversas = Object.values(historico).filter((lista) => Array.isArray(lista) && lista.length > 0).length;
    const pendentes = clientes.filter((cliente) => String(cliente.statusmsg || '').toLowerCase() === 'pendente').length;
    const unread = Object.values(unreadByOs).reduce((acc, value) => acc + Number(value || 0), 0);

    return {
      clientes: clientes.length,
      filtrados: filteredClients.length,
      conversas,
      pendentes,
      unread,
      canaisOnline
    };
  }, [clientes, filteredClients, historico, unreadByOs, canaisOnline]);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });

    if (navigator.vibrate) {
      navigator.vibrate(type === 'error' ? [50, 40, 50] : 28);
    }

    window.clearTimeout(showToast._timer);
    showToast._timer = window.setTimeout(() => setToast(null), 3200);
  }, []);

  const scrollToBottom = useCallback(() => {
    window.setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 60);
  }, []);

  const notifyIncoming = useCallback((titulo, corpo) => {
    if (document.visibilityState === 'visible') return;

    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(titulo, {
          body: corpo,
          silent: true
        });
      }
    } catch {
      // ignora notificação bloqueada pelo navegador
    }
  }, []);

  const addMessageToHistory = useCallback((osId, message) => {
    if (!osId || !message) return false;

    const key = buildMessageKey(message);
    const stableKey = getMessageStableKey(message);

    if (receivedKeysRef.current.has(key) || receivedKeysRef.current.has(stableKey)) return false;

    receivedKeysRef.current.add(key);
    receivedKeysRef.current.add(stableKey);

    if (receivedKeysRef.current.size > 1200) {
      receivedKeysRef.current = new Set(Array.from(receivedKeysRef.current).slice(-700));
    }

    setHistorico((prev) => {
      const atual = prev[osId] || [];
      const proximo = mergeMessageLists(atual, [message]);

      return {
        ...prev,
        [osId]: proximo
      };
    });

    return true;
  }, []);

  const aplicarCanaisNoEstado = useCallback((lista = []) => {
    const normalizados = lista
      .map((canal) => normalizarCanalWhatsapp(canal, empresaIdAtual))
      .filter((canal) => canal.id);

    setCanais(normalizados);
    return normalizados;
  }, [empresaIdAtual]);

  const fetchCanais = useCallback(async () => {
    setLoadingCanais(true);

    try {
      const response = await api.get('/api/whatsapp/canais');
      const lista = resolverArrayApi(response.data, ['canais', 'channels']);
      const normalizados = aplicarCanaisNoEstado(lista);

      setPlanoCanais({
        planoNome: response.data?.planoNome || response.data?.resumoPlano?.planoNome || 'Plano atual',
        limiteCanais: Number(response.data?.limiteCanais || response.data?.resumoPlano?.limiteCanais || response.data?.resumoPlano?.limiteEmpresas || Math.max(1, normalizados.length)),
        total: Number(response.data?.total ?? normalizados.length),
        disponiveis: Number(response.data?.disponiveis ?? Math.max(0, Number(response.data?.limiteCanais || Math.max(1, normalizados.length)) - normalizados.length)),
        podeCriar: response.data?.podeCriar !== undefined ? Boolean(response.data.podeCriar) : true
      });

      return normalizados;
    } catch (error) {
      const props = Array.isArray(canaisWhatsappProp) ? canaisWhatsappProp : [];

      if (props.length) {
        return aplicarCanaisNoEstado(props);
      }

      setCanais([]);
      return [];
    } finally {
      setLoadingCanais(false);
    }
  }, [aplicarCanaisNoEstado, canaisWhatsappProp]);

  const fetchEmpresasGrupo = useCallback(async () => {
    try {
      const response = await api.get('/empresas', {
        params: {
          comResumo: true
        }
      });

      const lista = resolverArrayApi(response.data, ['empresas', 'items'])
        .map(normalizarEmpresaGrupo)
        .filter(Boolean);

      setEmpresasGrupo(lista);
      return lista;
    } catch {
      setEmpresasGrupo([]);
      return [];
    }
  }, []);

  const fetchClientes = useCallback(async () => {
    setLoadingClients(true);

    try {
      let response;

      try {
        response = await api.get('/ordens-servico', {
          params: {
            empresaMatrizId: empresaIdAtual || undefined,
            incluirFiliais: true
          }
        });
      } catch {
        response = await api.get('/ordens-servico', {
          params: empresaIdAtual ? { empresaId: empresaIdAtual } : {}
        });
      }

      const lista = resolverArrayApi(response.data, ['ordens', 'ordensServico', 'items']);
      const validos = lista
        .filter((os) => os.whatsapp || os.cliente?.telefone)
        .map(normalizarOrdemServico)
        .filter((os) => os.id && os.whatsappClean);

      setClientes(validos);

      if (!selectedIdRef.current && validos.length > 0) {
        setSelectedId(validos[0].id);
      }

      return validos;
    } catch (error) {
      showToast('error', montarErroApi(error));
      return [];
    } finally {
      setLoadingClients(false);
    }
  }, [empresaIdAtual, showToast]);

  const fetchHistory = useCallback(async (osId, options = {}) => {
    if (!osId) return [];

    const silent = options.silent === true;

    if (!silent) {
      setLoadingHistory(true);
    }

    try {
      const cliente = clientesRef.current.find((item) => item.id === osId);
      const canalAutomatico = escolherCanalParaCliente(cliente, canaisRef.current, selectedCanalIdRef.current === 'auto' ? null : canaisRef.current.find((c) => c.id === selectedCanalIdRef.current));
      const canalHistorico = canalAutomatico;

      if (!canalHistorico?.id || !isMongoId(canalHistorico.id)) {
        /*
          Não limpa o chat local se a API/canal não estiver disponível.
          Isso evita sumir mensagem recebida via socket.
        */
        return [];
      }

      const response = await api.get(`/api/whatsapp/canais/${canalHistorico.id}/history/${osId}`, {
        params: {
          empresaId: canalHistorico.empresaId || getClienteEmpresaId(cliente) || empresaIdAtual || undefined,
          empresaMatrizId: cliente?.empresaMatrizId || empresaIdAtual || undefined,
          canalWhatsappId: canalHistorico.id,
          number: getClienteTelefone(cliente),
          whatsapp: getClienteTelefone(cliente)
        }
      });

      const apiChat = resolverArrayApi(response.data, ['messages', 'historico'])
        .map(getMessageFromApi)
        .filter((m) => m.text || m.type === 'event');

      const timelineLogs = (cliente?.registros || [])
        .filter((r) => r?.texto)
        .map((r) => ({
          id: `reg_${r._id || r.createdAt || Math.random()}`,
          text: r.texto,
          sender: String(r.tipo || '').toLowerCase() === 'whatsapp' ? 'bot' : 'system',
          time: formatTime(r.createdAt),
          rawDate: r.createdAt,
          type: String(r.tipo || '').toLowerCase() === 'whatsapp' ? 'chat' : 'event',
          status: 'sent',
          success: true
        }));

      const unified = [...apiChat, ...timelineLogs]
        .sort((a, b) => new Date(a.rawDate || 0) - new Date(b.rawDate || 0));

      setHistorico((prev) => {
        const atual = prev[osId] || [];
        const merged = mergeMessageLists(atual, unified);

        merged.forEach((message) => {
          receivedKeysRef.current.add(buildMessageKey(message));
        });

        return {
          ...prev,
          [osId]: merged
        };
      });

      if (!silent) scrollToBottom();

      return unified;
    } catch (error) {
      if (!silent) {
        showToast('error', montarErroApi(error));
      }

      return [];
    } finally {
      if (!silent) {
        setLoadingHistory(false);
      }
    }
  }, [empresaIdAtual, scrollToBottom, showToast]);

  const handleSelectClient = useCallback((id) => {
    setSelectedId(id);
    setSelectedCanalId('auto');
    setShowMobileList(false);

    setUnreadByOs((prev) => ({
      ...prev,
      [id]: 0
    }));
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchEmpresasGrupo(), fetchCanais(), fetchClientes()]);

    if (selectedIdRef.current) {
      await fetchHistory(selectedIdRef.current, { silent: true });
    }

    showToast('success', 'CRM atualizado.');
  }, [fetchEmpresasGrupo, fetchCanais, fetchClientes, fetchHistory, showToast]);

  const handleSend = useCallback(async () => {
    const mensagem = typedMessage.trim();

    if (!mensagem || !selectedClient || sending) return;

    const canalSelecionado =
      selectedCanalId === 'auto'
        ? null
        : canais.find((canal) => canal.id === selectedCanalId);

    const canalParaEnvio = escolherCanalParaCliente(selectedClient, canais, canalSelecionado || canalPrincipalLocal);

    if (!canalParaEnvio?.id || !isMongoId(canalParaEnvio.id)) {
      showToast('error', 'Nenhum canal válido para essa loja. Configure na tela Empresas.');
      return;
    }

    if (!canalParaEnvio.connected) {
      showToast('error', 'Canal desconectado. Conecte o WhatsApp na tela Empresas.');
      return;
    }

    if (selectedClient.isSemVinculo && !(selectedClient.whatsappId || selectedClient.remoteJid)) {
      showToast('error', 'Essa conversa sem vínculo não possui whatsappId para resposta segura.');
      return;
    }

    const osId = selectedClient.isSemVinculo ? '' : selectedClient.id;
    const conversaId = selectedClient.id;
    const tempId = `tmp_${Date.now()}`;
    const now = new Date().toISOString();

    const optimistic = {
      id: tempId,
      text: mensagem,
      sender: 'me',
      time: formatTime(now),
      rawDate: now,
      type: 'chat',
      status: 'pending',
      success: true,
      canalWhatsappId: canalParaEnvio.id
    };

    setTypedMessage('');
    setSending(true);

    addMessageToHistory(conversaId, optimistic);
    scrollToBottom();

    try {
      const payload = {
        number: selectedClient.isSemVinculo ? undefined : selectedClient.whatsappClean,
        whatsappId: selectedClient.whatsappId || selectedClient.remoteJid || undefined,
        chatId: selectedClient.whatsappId || selectedClient.remoteJid || undefined,
        remoteJid: selectedClient.whatsappId || selectedClient.remoteJid || undefined,
        message: mensagem,
        osId: osId || undefined,
        ordemServicoId: osId || undefined,
        empresaId: canalParaEnvio.empresaId || getClienteEmpresaId(selectedClient) || empresaIdAtual || undefined,
        unidadeId: canalParaEnvio.unidadeId || getClienteEmpresaId(selectedClient) || undefined,
        canalWhatsappId: canalParaEnvio.id,
        loja: getCodigoLoja(selectedClient) || undefined,
        codigoLoja: getCodigoLoja(selectedClient) || undefined,
        origem: selectedClient.isSemVinculo ? 'crm_whatsapp_sem_vinculo' : 'crm_whatsapp',
        tipoMensagem: selectedClient.isSemVinculo ? 'manual_crm_sem_vinculo' : 'manual_crm',
        referenceContext: {
          osId: osId || undefined,
          ordemServicoId: osId || undefined,
          empresaId: canalParaEnvio.empresaId || getClienteEmpresaId(selectedClient) || empresaIdAtual || undefined,
          unidadeId: canalParaEnvio.unidadeId || getClienteEmpresaId(selectedClient) || undefined,
          canalWhatsappId: canalParaEnvio.id,
          whatsappId: selectedClient.whatsappId || selectedClient.remoteJid || undefined,
          origem: selectedClient.isSemVinculo ? 'crm_whatsapp_sem_vinculo' : 'crm_whatsapp',
          tipoMensagem: selectedClient.isSemVinculo ? 'manual_crm_sem_vinculo' : 'manual_crm'
        }
      };

      const response = await api.post(`/api/whatsapp/canais/${canalParaEnvio.id}/send`, payload);
      const serializedId = response?.data?.data?.serializedId || response?.data?.serializedId || tempId;

      setHistorico((prev) => ({
        ...prev,
        [conversaId]: (prev[conversaId] || []).map((message) => (
          message.id === tempId
            ? {
                ...message,
                id: serializedId,
                serializedId,
                status: 'sent',
                success: true
              }
            : message
        ))
      }));

      if (osId) {
        setTimeout(() => {
          fetchHistory(osId, { silent: true }).catch(() => {});
        }, 700);
      }
    } catch (error) {
      setHistorico((prev) => ({
        ...prev,
        [conversaId]: (prev[conversaId] || []).map((message) => (
          message.id === tempId
            ? {
                ...message,
                status: 'failed',
                success: false,
                errorMessage: montarErroApi(error)
              }
            : message
        ))
      }));

      showToast('error', montarErroApi(error));
    } finally {
      setSending(false);
    }
  }, [
    typedMessage,
    selectedClient,
    sending,
    selectedCanalId,
    canais,
    canalPrincipalLocal,
    empresaIdAtual,
    addMessageToHistory,
    scrollToBottom,
    fetchHistory,
    showToast
  ]);

  const vincularConversaAOS = useCallback(async (osDestino) => {
    if (!selectedClient?.isSemVinculo || !osDestino?.id || vinculandoId) return;

    const canalParaVinculo =
      canais.find((canal) => canal.id === selectedClient.canalWhatsappId) ||
      canalPreview ||
      canalPrincipalLocal;

    if (!canalParaVinculo?.id || !isMongoId(canalParaVinculo.id)) {
      showToast('error', 'Não encontrei o canal dessa conversa sem vínculo.');
      return;
    }

    const whatsappId = selectedClient.whatsappId || selectedClient.remoteJid;

    if (!whatsappId) {
      showToast('error', 'Essa conversa não possui whatsappId para vincular.');
      return;
    }

    setVinculandoId(osDestino.id);

    try {
      const response = await api.post(
        `/api/whatsapp/canais/${canalParaVinculo.id}/conversas/vincular-os`,
        {
          osId: osDestino.id,
          whatsappId,
          chatId: whatsappId,
          remoteJid: whatsappId,
          number: selectedClient.whatsappClean || selectedClient.whatsapp || ''
        }
      );

      const conversaOrigemId = selectedClient.id;
      const mensagensOrigem = historico[conversaOrigemId] || [];

      setHistorico((prev) => {
        const mensagensDestino = prev[osDestino.id] || [];
        const mensagensApi = resolverArrayApi(response.data, ['messages', 'mensagens'])
          .map(getMessageFromApi)
          .filter((msg) => msg.text);

        const merged = mergeMessageLists(mensagensDestino, [
          ...mensagensOrigem,
          ...mensagensApi
        ]);

        const next = {
          ...prev,
          [osDestino.id]: merged
        };

        delete next[conversaOrigemId];

        return next;
      });

      setClientes((prev) =>
        prev
          .filter((cliente) => cliente.id !== conversaOrigemId)
          .map((cliente) => (
            cliente.id === osDestino.id
              ? {
                  ...cliente,
                  whatsappId,
                  remoteJid: whatsappId,
                  canalWhatsappId: canalParaVinculo.id
                }
              : cliente
          ))
      );

      setUnreadByOs((prev) => {
        const next = {
          ...prev,
          [osDestino.id]: Number(prev[osDestino.id] || 0) + Number(prev[conversaOrigemId] || 0)
        };

        delete next[conversaOrigemId];

        return next;
      });

      setMensagensSemVinculo((prev) =>
        prev.filter((msg) => String(msg.whatsappId || msg.remoteJid || '') !== String(whatsappId))
      );

      setShowVincularModal(false);
      setVinculoSearch('');
      setSelectedId(osDestino.id);

      setTimeout(() => {
        fetchHistory(osDestino.id, { silent: true }).catch(() => {});
      }, 500);

      showToast('success', 'Conversa vinculada à O.S. com sucesso.');
    } catch (error) {
      showToast('error', montarErroApi(error));
    } finally {
      setVinculandoId('');
    }
  }, [
    selectedClient,
    vinculandoId,
    canais,
    canalPreview,
    canalPrincipalLocal,
    historico,
    fetchHistory,
    showToast
  ]);

  const aplicarTemplate = useCallback((texto) => {
    if (!selectedClient) return;

    setTypedMessage((prev) => {
      const prefixo = prev.trim() ? `${prev.trim()}\n\n` : '';
      return `${prefixo}${replaceTemplateVars(texto, selectedClient)}`;
    });

    setShowTemplates(false);
  }, [selectedClient]);

  useEffect(() => {
    const props = Array.isArray(canaisWhatsappProp) ? canaisWhatsappProp : [];

    if (props.length) {
      aplicarCanaisNoEstado(props);
    }
  }, [canaisWhatsappProp, aplicarCanaisNoEstado]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (selectedId) {
      fetchHistory(selectedId);
    }
  }, [selectedId, fetchHistory]);

  useEffect(() => {
    const token =
      localStorage.getItem('lumixToken') ||
      localStorage.getItem('vizumToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      '';

    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],
      auth: token ? { token } : undefined,
      query: empresaIdAtual ? { empresaId: empresaIdAtual } : undefined,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 800
    });

    const handleStatus = (payload = {}) => {
      const canalId = normalizarId(payload.canalWhatsappId || payload.canalId);
      if (!canalId) return;

      const status = normalizarStatusBackend(payload.status, payload.connected);

      setCanais((prev) => prev.map((canal) => {
        if (canal.id !== canalId) return canal;

        return {
          ...canal,
          status,
          connected: status === 'CONNECTED',
          sessionName: payload.sessionName || canal.sessionName,
          lastStatusAt: payload.timestamp || payload.checkedAt || new Date().toISOString()
        };
      }));

      if (status === 'DISCONNECTED') {
        setTimeout(() => fetchCanais(), 700);
      }
    };

    const handleIncoming = (data = {}) => {
      const osIdPayload = extrairOsIdDoPayloadMensagem(data);
      const numeroMensagem = normalizarNumero(data.from || data.number || data.whatsappId || data.telefone || '');
      const clientesAtuais = clientesRef.current;
      const canaisAtuais = canaisRef.current;
      const canalId = normalizarId(data.canalWhatsappId || data.channelId || data.canalId);

      const clientePeloNumero = numeroMensagem
        ? clientesAtuais.find((cliente) => {
            const numeroCliente = getClienteTelefone(cliente);
            if (!numeroCliente) return false;

            return (
              numeroCliente === numeroMensagem ||
              numeroCliente.endsWith(numeroMensagem.slice(-10)) ||
              numeroMensagem.endsWith(numeroCliente.slice(-10))
            );
          })
        : null;

      /*
        Regra profissional:
        NUNCA joga mensagem no chat aberto só porque veio do mesmo canal.
        Se não tiver osId/referenceContext e não bater pelo telefone, vira uma
        conversa sem vínculo separada.
      */
      const targetId = osIdPayload || clientePeloNumero?.id || '';

      if (!targetId) {
        const canalOrigem = canaisAtuais.find((canal) => canal.id === canalId) || null;
        const clienteSemVinculo = montarClienteSemVinculo(data, canalOrigem);
        const conversaSemVinculoId = clienteSemVinculo.id;

        const unlinkedMessage = {
          id: data.serializedId || data.context?.dbId || `${canalId || 'canal'}-${data.rawDate || Date.now()}-${data.body || ''}`,
          serializedId: data.serializedId || '',
          text: data.body || data.message || data.text || '[Mensagem recebida]',
          sender: data.sender || 'client',
          time: formatTime(data.rawDate || data.sentAt || new Date()),
          rawDate: data.rawDate || data.sentAt || new Date().toISOString(),
          type: 'chat',
          status: data.sender === 'me' ? 'sent' : 'received',
          success: true,
          canalWhatsappId: canalId,
          isSemVinculo: true,
          empresaId: data.empresaId || '',
          whatsappId: getWhatsappIdRemetente(data),
          remoteJid: getWhatsappIdRemetente(data),
          from: data.from || data.whatsappId || ''
        };

        setClientes((prev) => {
          const existe = prev.some((cliente) => String(cliente.id) === String(conversaSemVinculoId));

          if (existe) {
            return prev.map((cliente) => (
              String(cliente.id) === String(conversaSemVinculoId)
                ? {
                    ...cliente,
                    updatedAt: unlinkedMessage.rawDate,
                    statusmsg: 'Nova mensagem'
                  }
                : cliente
            ));
          }

          return [clienteSemVinculo, ...prev];
        });

        setMensagensSemVinculo((prev) => [unlinkedMessage, ...prev].slice(0, 50));

        addMessageToHistory(conversaSemVinculoId, unlinkedMessage);

        if (String(conversaSemVinculoId) !== String(selectedIdRef.current) && unlinkedMessage.sender !== 'me') {
          setUnreadByOs((prev) => ({
            ...prev,
            [conversaSemVinculoId]: Number(prev[conversaSemVinculoId] || 0) + 1
          }));
        }

        showToast('success', 'Nova conversa sem O.S. vinculada.');
        notifyIncoming('Nova conversa no WhatsApp', unlinkedMessage.text);

        return;
      }

      const message = {
        id: data.serializedId || data.context?.dbId || `${canalId || 'canal'}-${data.rawDate || Date.now()}-${data.body || ''}`,
        serializedId: data.serializedId || '',
        text: data.body || data.message || data.text || '[Mensagem recebida]',
        sender: data.sender || 'client',
        time: formatTime(data.rawDate || data.sentAt || new Date()),
        rawDate: data.rawDate || data.sentAt || new Date().toISOString(),
        type: 'chat',
        status: 'received',
        success: true,
        canalWhatsappId: canalId
      };

      const added = addMessageToHistory(String(targetId), message);

      if (!added) return;

      if (String(targetId) === String(selectedIdRef.current)) {
        scrollToBottom();

        setTimeout(() => {
          fetchHistory(String(targetId), { silent: true }).catch(() => {});
        }, 700);
      } else if (message.sender !== 'me') {
        setUnreadByOs((prev) => ({
          ...prev,
          [targetId]: Number(prev[targetId] || 0) + 1
        }));

        const clienteDestino = clientesAtuais.find((cliente) => String(cliente.id) === String(targetId));
        const nomeCliente = clienteDestino ? getClienteNome(clienteDestino) : 'Cliente';

        showToast('success', `Nova resposta de ${nomeCliente}.`);
        notifyIncoming(`Nova resposta de ${nomeCliente}`, message.text);
      }
    };

    const statusEvents = ['whatsapp_status'];
    const messageEvents = ['incoming_message', 'whatsapp_message', 'message_received', 'mensagem_recebida', 'whatsapp_unlinked_message'];

    statusEvents.forEach((event) => socketRef.current.on(event, handleStatus));
    messageEvents.forEach((event) => socketRef.current.on(event, handleIncoming));

    if (empresaIdAtual) {
      statusEvents.forEach((event) => {
        socketRef.current.on(`${event}:${empresaIdAtual}`, handleStatus);
        socketRef.current.on(`${event}_${empresaIdAtual}`, handleStatus);
      });

      messageEvents.forEach((event) => {
        socketRef.current.on(`${event}:${empresaIdAtual}`, handleIncoming);
        socketRef.current.on(`${event}_${empresaIdAtual}`, handleIncoming);
      });
    }

    return () => {
      statusEvents.forEach((event) => socketRef.current?.off(event, handleStatus));
      messageEvents.forEach((event) => socketRef.current?.off(event, handleIncoming));

      if (empresaIdAtual) {
        statusEvents.forEach((event) => {
          socketRef.current?.off(`${event}:${empresaIdAtual}`, handleStatus);
          socketRef.current?.off(`${event}_${empresaIdAtual}`, handleStatus);
        });

        messageEvents.forEach((event) => {
          socketRef.current?.off(`${event}:${empresaIdAtual}`, handleIncoming);
          socketRef.current?.off(`${event}_${empresaIdAtual}`, handleIncoming);
        });
      }

      socketRef.current?.disconnect();
    };
  }, [socketUrl, empresaIdAtual, fetchCanais, fetchHistory, addMessageToHistory, scrollToBottom, showToast, notifyIncoming]);

  useEffect(() => {
    if (!selectedId || !selectedClient) return undefined;

    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchHistory(selectedId, { silent: true }).catch(() => {});
    }, 18000);

    return () => clearInterval(interval);
  }, [selectedId, selectedClient, fetchHistory]);

  const chatEmpty = currentChat.length === 0;
  const mobileSidebarOpen = showMobileList || !selectedClient;

  return (
    <div className="lumix-crm">
      <style>{styles}</style>

      <section className="crm-shell">
        <aside className={`crm-sidebar ${mobileSidebarOpen ? 'is-open' : ''}`}>
          <div className="crm-sidebar-head">
            <div className="crm-title-block">
              <span>CRM WhatsApp</span>
              <h2>Pós-venda</h2>
              <p>Histórico, mensagens e retorno do cliente por loja.</p>
            </div>

            <button className="icon-button" type="button" onClick={refreshAll} title="Atualizar">
              <Icons.Refresh />
            </button>
          </div>

          <div className="crm-metrics">
            <div>
              <strong>{stats.clientes}</strong>
              <span>Clientes</span>
            </div>
            <div>
              <strong>{stats.canaisOnline}</strong>
              <span>Canais online</span>
            </div>
            <div>
              <strong>{stats.unread}</strong>
              <span>Novas</span>
            </div>
          </div>

          {mensagensSemVinculo.length > 0 && (
            <div className="unlinked-alert">
              <strong>{mensagensSemVinculo.length}</strong>
              <span>mensagem(ns) sem O.S. vinculada. Abra o WhatsApp/cliente e envie pelo CRM para criar vínculo seguro.</span>
            </div>
          )}

          <div className="channel-line">
            <div className={`channel-led ${canaisOnline > 0 ? 'online' : ''}`} />
            <div>
              <strong>{canaisOnline > 0 ? 'Operação conectada' : 'Nenhum canal conectado'}</strong>
              <span>{canaisOnline}/{canais.length} canais ativos · {planoCanais.planoNome}</span>
            </div>
            <button type="button" onClick={() => abrirTelaEmpresasCanal(canalPreview, empresaIdAtual)}>
              Gerenciar
            </button>
          </div>

          <div className="channel-mini-list">
            {loadingCanais && canaisResumo.length === 0 ? (
              <div className="mini-loading">Carregando canais...</div>
            ) : canaisResumo.length > 0 ? (
              canaisResumo.map((canal) => (
                <button
                  type="button"
                  key={canal.id}
                  className={`channel-mini ${canal.connected ? 'online' : ''}`}
                  onClick={() => abrirTelaEmpresasCanal(canal, canal.empresaId || empresaIdAtual)}
                >
                  <span />
                  <div>
                    <strong>{canal.codigoLoja || canal.unidadeNome || canal.nome}</strong>
                    <small>{canal.connected ? 'Conectado' : 'Desconectado'}</small>
                  </div>
                </button>
              ))
            ) : (
              <div className="mini-empty">Nenhum canal cadastrado.</div>
            )}
          </div>

          <div className="filters-card">
            <label className="search-field">
              <Icons.Search />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Cliente, OS ou telefone"
              />
            </label>

            <div className="filter-row">
              <label>
                <span>Status</span>
                <select value={statusFiltro} onChange={(event) => setStatusFiltro(event.target.value)}>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Loja</span>
                <select value={lojaFiltro} onChange={(event) => setLojaFiltro(event.target.value)}>
                  {lojaOptions.map((loja) => (
                    <option key={loja} value={loja}>{loja}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="filter-row">
              <label>
                <span>Canal</span>
                <select value={canalFiltro} onChange={(event) => setCanalFiltro(event.target.value)}>
                  <option value="todos">Todos</option>
                  {canais.map((canal) => (
                    <option key={canal.id} value={canal.id}>
                      {canal.connected ? '🟢' : '⚪'} {canal.codigoLoja || canal.unidadeNome}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Envio</span>
                <select value={selectedCanalId} onChange={(event) => setSelectedCanalId(event.target.value)}>
                  <option value="auto">Automático</option>
                  {canais.map((canal) => (
                    <option key={canal.id} value={canal.id}>
                      {canal.connected ? '🟢' : '⚪'} {canal.codigoLoja || canal.unidadeNome}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="contacts-list">
            {loadingClients && filteredClients.length === 0 && <ContactSkeletonList />}

            {!loadingClients && filteredClients.length === 0 && (
              <div className="empty-list">
                <Icons.Message />
                <strong>Nenhum cliente encontrado</strong>
                <p>Altere os filtros ou atualize a lista.</p>
              </div>
            )}

            {filteredClients.map((cliente) => {
              const active = cliente.id === selectedId;
              const chat = historico[cliente.id] || [];
              const last = chat[chat.length - 1];
              const unread = unreadByOs[cliente.id] || 0;
              const canalCliente = escolherCanalParaCliente(cliente, canais, null);

              return (
                <button
                  type="button"
                  className={`contact-card ${active ? 'active' : ''}`}
                  key={cliente.id}
                  onClick={() => handleSelectClient(cliente.id)}
                >
                  <div className="avatar">{initials(getClienteNome(cliente))}</div>

                  <div className="contact-copy">
                    <div className="contact-top">
                      <strong>{getClienteNome(cliente)}</strong>
                      <span>{last ? last.time : formatDateShort(cliente.dataCompra || cliente.createdAt)}</span>
                    </div>

                    <p>
                      {last
                        ? `${last.sender === 'me' ? 'Você: ' : ''}${last.text}`
                        : `OS #${cliente.numeroOS || cliente.produto?.numeroCV || 'S/N'} · ${cliente.status || 'Sem status'}`}
                    </p>

                    <div className="tags">
                      <span>{cliente.isSemVinculo ? 'sem vínculo' : getCodigoLoja(cliente) || 'sem-loja'}</span>
                      <span className={canalCliente?.connected ? 'online' : ''}>{canalCliente?.connected ? 'canal online' : 'sem canal online'}</span>
                    </div>
                  </div>

                  {unread > 0 && <span className="unread-badge">{unread}</span>}
                </button>
              );
            })}
          </div>
        </aside>

        <main className="crm-main">
          {selectedClient ? (
            <>
              <header className="chat-header">
                <button type="button" className="mobile-back" onClick={() => setShowMobileList(true)}>
                  <Icons.Back />
                </button>

                <div className="avatar large">{initials(getClienteNome(selectedClient))}</div>

                <div className="chat-title">
                  <h3>{getClienteNome(selectedClient)}</h3>
                  <span>
                    {selectedClient.isSemVinculo
                      ? 'Conversa sem O.S. vinculada'
                      : `OS #${selectedClient.numeroOS || selectedClient.produto?.numeroCV || 'S/N'} · Loja ${getCodigoLoja(selectedClient) || 'S/C'}`}
                  </span>
                </div>

                <div className="chat-context">
                  <div className={`context-chip ${canalPreview?.connected ? 'online' : 'offline'}`}>
                    <small>Canal</small>
                    <strong>{canalPreview ? canalPreview.codigoLoja || canalPreview.unidadeNome : 'Sem canal'}</strong>
                  </div>

                  <div className="context-chip">
                    <small>Empresa</small>
                    <strong>{getClienteEmpresaNome(selectedClient)}</strong>
                  </div>
                </div>

                <a className="icon-button phone" href={`tel:${selectedClient.whatsappClean}`} title="Ligar">
                  <Icons.Phone />
                </a>
              </header>

              <section className="chat-body">
                {loadingHistory && chatEmpty && (
                  <div className="chat-loading">
                    <span />
                    <p>Buscando histórico...</p>
                  </div>
                )}

                {loadingHistory && !chatEmpty && (
                  <div className="soft-refresh">Atualizando conversa...</div>
                )}

                {!loadingHistory && chatEmpty && (
                  <div className="chat-empty">
                    <Icons.Message />
                    <h3>Histórico vazio</h3>
                    <p>Envie a primeira mensagem para iniciar a conversa deste cliente.</p>
                  </div>
                )}

                {Object.entries(groupedChat).map(([date, messages]) => (
                  <div className="day-group" key={date}>
                    <div className="day-chip">{date}</div>

                    {messages.map((message) => (
                      <div className={`bubble-row ${message.sender === 'me' ? 'me' : message.sender === 'system' ? 'system' : 'client'}`} key={message.id || buildMessageKey(message)}>
                        {message.sender === 'system' || message.type === 'event' ? (
                          <div className="system-event">{message.text}</div>
                        ) : (
                          <div className={`bubble ${message.sender === 'me' ? 'me' : 'client'}`}>
                            <p>{message.text}</p>
                            <div className="bubble-meta">
                              <span>{message.time || formatTime(message.rawDate)}</span>
                              {message.sender === 'me' && <MessageStatus message={message} />}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}

                <div ref={chatEndRef} />
              </section>

              <footer className="composer">
                <div className="template-row">
                  <button type="button" onClick={() => setShowTemplates((prev) => !prev)}>
                    <Icons.Spark />
                    Modelos rápidos
                  </button>

                  {selectedClient.isSemVinculo && (
                    <button type="button" className="link-os-button" onClick={() => setShowVincularModal(true)}>
                      <Icons.Check />
                      Vincular a uma O.S.
                    </button>
                  )}

                  <button type="button" onClick={() => abrirTelaEmpresasCanal(canalPreview, getClienteEmpresaId(selectedClient) || empresaIdAtual)}>
                    <Icons.Building />
                    Gerenciar canal
                  </button>
                </div>

                {showTemplates && (
                  <div className="templates-popover">
                    {QUICK_TEMPLATES.map((template) => (
                      <button type="button" key={template.id} onClick={() => aplicarTemplate(template.texto)}>
                        <strong>{template.titulo}</strong>
                        <span>{replaceTemplateVars(template.texto, selectedClient)}</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="composer-box">
                  <textarea
                    value={typedMessage}
                    onChange={(event) => setTypedMessage(event.target.value)}
                    placeholder={`Mensagem para ${firstName(getClienteNome(selectedClient))}`}
                    rows={1}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="send-button"
                    disabled={!typedMessage.trim() || sending}
                    onClick={handleSend}
                  >
                    {sending ? <span className="button-spinner" /> : <Icons.Send />}
                  </button>
                </div>
              </footer>
            </>
          ) : (
            <div className="no-chat">
              <Icons.Message />
              <h2>Selecione um cliente</h2>
              <p>O CRM mostra o histórico, o canal correto e as respostas em tempo real.</p>
            </div>
          )}
        </main>
      </section>

      {showVincularModal && selectedClient?.isSemVinculo && (
        <div className="modal-backdrop" onClick={() => setShowVincularModal(false)}>
          <div className="link-modal" onClick={(event) => event.stopPropagation()}>
            <div className="link-modal-head">
              <div>
                <span>Vincular conversa</span>
                <h3>Escolha a O.S. correta</h3>
                <p>
                  Essa conversa passará a fazer parte do histórico da O.S. escolhida.
                </p>
              </div>

              <button type="button" onClick={() => setShowVincularModal(false)}>
                ×
              </button>
            </div>

            <label className="link-search">
              <Icons.Search />
              <input
                value={vinculoSearch}
                onChange={(event) => setVinculoSearch(event.target.value)}
                placeholder="Buscar por cliente, OS, loja ou telefone"
                autoFocus
              />
            </label>

            <div className="link-current">
              <strong>Conversa atual</strong>
              <span>{selectedClient.whatsappId || selectedClient.remoteJid || selectedClient.whatsapp || 'Sem identificador'}</span>
            </div>

            <div className="link-results">
              {osVinculoCandidates.length === 0 ? (
                <div className="link-empty">Nenhuma O.S. encontrada para esse filtro.</div>
              ) : (
                osVinculoCandidates.map((cliente) => (
                  <button
                    type="button"
                    key={cliente.id}
                    className="link-result-card"
                    onClick={() => vincularConversaAOS(cliente)}
                    disabled={Boolean(vinculandoId)}
                  >
                    <div className="avatar">{initials(getClienteNome(cliente))}</div>

                    <div>
                      <strong>{getClienteNome(cliente)}</strong>
                      <span>
                        OS #{cliente.numeroOS || cliente.produto?.numeroCV || 'S/N'} · Loja {getCodigoLoja(cliente) || 'S/C'}
                      </span>
                    </div>

                    <em>{vinculandoId === cliente.id ? 'Vinculando...' : 'Vincular'}</em>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

const styles = `
  :root {
    --crm-bg: #f6f8fb;
    --crm-surface: #ffffff;
    --crm-surface-2: #f8fafc;
    --crm-line: #e5e7eb;
    --crm-line-soft: #eef2f7;
    --crm-text: #17212f;
    --crm-muted: #667085;
    --crm-soft: #98a2b3;
    --crm-green: #188038;
    --crm-green-soft: #eaf7ee;
    --crm-blue: #2563eb;
    --crm-red: #dc2626;
    --crm-shadow: 0 18px 50px rgba(15, 23, 42, .08);
  }

  .lumix-crm {
    width: 100%;
    min-height: 100dvh;
    background: var(--crm-bg);
    color: var(--crm-text);
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .crm-shell {
    height: 100dvh;
    display: grid;
    grid-template-columns: 390px minmax(0, 1fr);
    overflow: hidden;
  }

  .crm-sidebar {
    min-width: 0;
    border-right: 1px solid var(--crm-line);
    background: rgba(255,255,255,.92);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .crm-sidebar-head {
    padding: 18px 18px 12px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
  }

  .crm-title-block span {
    display: block;
    font-size: 11px;
    letter-spacing: .08em;
    color: var(--crm-green);
    font-weight: 700;
    text-transform: uppercase;
  }

  .crm-title-block h2 {
    margin: 3px 0 4px;
    font-size: 21px;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -.03em;
  }

  .crm-title-block p {
    margin: 0;
    font-size: 12px;
    line-height: 1.45;
    color: var(--crm-muted);
  }

  .icon-button {
    width: 38px;
    height: 38px;
    border: 1px solid var(--crm-line);
    border-radius: 12px;
    background: #fff;
    color: #475467;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: .18s ease;
    flex: 0 0 auto;
  }

  .icon-button:hover {
    background: var(--crm-surface-2);
    transform: translateY(-1px);
  }

  .crm-metrics {
    padding: 0 18px 12px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .crm-metrics div {
    background: #fff;
    border: 1px solid var(--crm-line-soft);
    border-radius: 14px;
    padding: 10px 11px;
    display: grid;
    gap: 2px;
  }

  .crm-metrics strong {
    font-size: 17px;
    line-height: 1;
  }

  .crm-metrics span {
    font-size: 11px;
    color: var(--crm-muted);
  }

  .channel-line {
    margin: 0 18px 10px;
    padding: 12px;
    border: 1px solid var(--crm-line-soft);
    border-radius: 16px;
    background: #fff;
    display: grid;
    grid-template-columns: 12px 1fr auto;
    gap: 10px;
    align-items: center;
  }

  .channel-led {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: #cbd5e1;
  }

  .channel-led.online {
    background: var(--crm-green);
    box-shadow: 0 0 0 5px rgba(24, 128, 56, .10);
  }

  .channel-line strong {
    display: block;
    font-size: 12.5px;
    font-weight: 700;
  }

  .channel-line span {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: var(--crm-muted);
  }

  .channel-line button,
  .template-row button {
    border: 1px solid var(--crm-line);
    background: #fff;
    border-radius: 999px;
    padding: 7px 10px;
    font-size: 11px;
    font-weight: 700;
    color: #334155;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .channel-mini-list {
    padding: 0 18px 12px;
    display: grid;
    gap: 7px;
  }

  .channel-mini,
  .mini-empty,
  .mini-loading {
    width: 100%;
    border: 1px solid var(--crm-line-soft);
    background: #fff;
    border-radius: 13px;
    padding: 9px 10px;
    display: grid;
    grid-template-columns: 9px 1fr;
    gap: 9px;
    align-items: center;
    text-align: left;
  }

  .channel-mini {
    cursor: pointer;
  }

  .channel-mini > span {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #cbd5e1;
  }

  .channel-mini.online > span {
    background: var(--crm-green);
  }

  .channel-mini strong {
    display: block;
    font-size: 12px;
  }

  .channel-mini small,
  .mini-empty,
  .mini-loading {
    font-size: 11px;
    color: var(--crm-muted);
  }

  .filters-card {
    margin: 0 18px 12px;
    display: grid;
    gap: 9px;
  }

  .search-field {
    height: 40px;
    border: 1px solid var(--crm-line);
    background: #fff;
    border-radius: 13px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    gap: 9px;
    color: var(--crm-muted);
  }

  .search-field input {
    width: 100%;
    border: 0;
    outline: 0;
    font-size: 13px;
    background: transparent;
    color: var(--crm-text);
  }

  .filter-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .filter-row label {
    min-width: 0;
    display: grid;
    gap: 4px;
  }

  .filter-row span {
    font-size: 10px;
    color: var(--crm-muted);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
  }

  .filter-row select {
    width: 100%;
    height: 36px;
    border: 1px solid var(--crm-line);
    border-radius: 12px;
    background: #fff;
    padding: 0 9px;
    outline: 0;
    font-size: 12px;
    color: var(--crm-text);
  }

  .contacts-list {
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    padding: 0 10px 12px 10px;
  }

  .contact-card {
    position: relative;
    width: 100%;
    display: grid;
    grid-template-columns: 42px 1fr auto;
    gap: 10px;
    padding: 11px 8px;
    border: 1px solid transparent;
    background: transparent;
    border-radius: 16px;
    cursor: pointer;
    text-align: left;
    transition: .16s ease;
  }

  .contact-card:hover {
    background: #f8fafc;
  }

  .contact-card.active {
    background: #eef7f0;
    border-color: #d3ecd9;
  }

  .avatar {
    width: 42px;
    height: 42px;
    border-radius: 999px;
    background: linear-gradient(135deg, #eaf7ee, #e9f1ff);
    color: #166534;
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 800;
  }

  .avatar.large {
    width: 44px;
    height: 44px;
  }

  .contact-copy {
    min-width: 0;
  }

  .contact-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
  }

  .contact-top strong {
    min-width: 0;
    font-size: 13.5px;
    font-weight: 700;
    color: var(--crm-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .contact-top span {
    font-size: 11px;
    color: var(--crm-soft);
    flex: 0 0 auto;
  }

  .contact-copy p {
    margin: 3px 0 7px;
    font-size: 12px;
    color: var(--crm-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tags {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
  }

  .tags span {
    font-size: 10.5px;
    border: 1px solid var(--crm-line-soft);
    background: #fff;
    color: #64748b;
    border-radius: 999px;
    padding: 3px 7px;
  }

  .tags .online {
    background: var(--crm-green-soft);
    color: var(--crm-green);
    border-color: #cdebd4;
  }

  .unread-badge {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--crm-green);
    color: #fff;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 800;
  }

  .crm-main {
    min-width: 0;
    min-height: 0;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    background:
      radial-gradient(circle at 20% 20%, rgba(24,128,56,.05), transparent 32%),
      #f4f7fb;
  }

  .chat-header {
    min-height: 72px;
    border-bottom: 1px solid var(--crm-line);
    background: rgba(255, 255, 255, .9);
    backdrop-filter: blur(14px);
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr) auto auto;
    gap: 12px;
    align-items: center;
    padding: 12px 18px;
  }

  .mobile-back {
    display: none;
  }

  .chat-title {
    min-width: 0;
  }

  .chat-title h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 750;
    letter-spacing: -.02em;
    color: var(--crm-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chat-title span {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: var(--crm-muted);
  }

  .chat-context {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .context-chip {
    border: 1px solid var(--crm-line);
    background: #fff;
    border-radius: 12px;
    padding: 7px 10px;
    display: grid;
    gap: 1px;
    min-width: 118px;
  }

  .context-chip small {
    font-size: 9.5px;
    font-weight: 800;
    color: var(--crm-soft);
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  .context-chip strong {
    font-size: 12px;
    color: var(--crm-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .context-chip.online {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }

  .context-chip.offline {
    background: #fff7ed;
    border-color: #fed7aa;
  }

  .chat-body {
    position: relative;
    min-height: 0;
    overflow-y: auto;
    padding: 22px 22px 18px;
  }

  .day-group {
    display: grid;
    gap: 8px;
  }

  .day-chip {
    width: fit-content;
    margin: 8px auto 10px;
    padding: 5px 10px;
    background: rgba(255, 255, 255, .78);
    border: 1px solid var(--crm-line-soft);
    border-radius: 999px;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
  }

  .bubble-row {
    display: flex;
    width: 100%;
    margin: 2px 0;
  }

  .bubble-row.me {
    justify-content: flex-end;
  }

  .bubble-row.client {
    justify-content: flex-start;
  }

  .bubble-row.system {
    justify-content: center;
  }

  .bubble {
    max-width: min(620px, 74%);
    border-radius: 18px;
    padding: 9px 10px 6px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, .04);
  }

  .bubble.client {
    background: #fff;
    border-bottom-left-radius: 6px;
  }

  .bubble.me {
    background: #d9fdd3;
    border-bottom-right-radius: 6px;
  }

  .bubble p {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font-size: 13.5px;
    line-height: 1.45;
  }

  .bubble-meta {
    margin-top: 4px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #667085;
  }

  .system-event {
    max-width: 520px;
    padding: 7px 10px;
    border-radius: 999px;
    background: rgba(255,255,255,.75);
    border: 1px solid var(--crm-line-soft);
    color: #667085;
    font-size: 11.5px;
    text-align: center;
  }

  .chat-empty,
  .no-chat,
  .empty-list,
  .chat-loading {
    height: 100%;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 8px;
    color: var(--crm-muted);
    text-align: center;
  }

  .chat-empty svg,
  .no-chat svg,
  .empty-list svg {
    color: #94a3b8;
  }

  .chat-empty h3,
  .no-chat h2,
  .empty-list strong {
    margin: 0;
    color: var(--crm-text);
  }

  .chat-empty p,
  .no-chat p,
  .empty-list p {
    margin: 0;
    max-width: 320px;
    font-size: 13px;
    line-height: 1.45;
  }

  .soft-refresh {
    position: sticky;
    top: 8px;
    z-index: 4;
    width: fit-content;
    margin: 0 auto 10px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, .08);
    background: rgba(255, 255, 255, .86);
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    backdrop-filter: blur(12px);
  }

  .composer {
    border-top: 1px solid var(--crm-line);
    background: rgba(255,255,255,.92);
    padding: 10px 18px 14px;
  }

  .template-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    overflow-x: auto;
  }

  .templates-popover {
    margin-bottom: 8px;
    background: #fff;
    border: 1px solid var(--crm-line);
    border-radius: 16px;
    padding: 8px;
    display: grid;
    gap: 6px;
    box-shadow: var(--crm-shadow);
  }

  .templates-popover button {
    width: 100%;
    border: 0;
    background: #fff;
    border-radius: 12px;
    padding: 10px;
    text-align: left;
    cursor: pointer;
  }

  .templates-popover button:hover {
    background: #f8fafc;
  }

  .templates-popover strong {
    display: block;
    font-size: 12.5px;
  }

  .templates-popover span {
    display: block;
    margin-top: 3px;
    color: var(--crm-muted);
    font-size: 12px;
    line-height: 1.35;
  }

  .composer-box {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 42px;
    gap: 9px;
    align-items: end;
  }

  .composer-box textarea {
    width: 100%;
    min-height: 42px;
    max-height: 130px;
    resize: vertical;
    border: 1px solid var(--crm-line);
    border-radius: 18px;
    background: #fff;
    padding: 11px 14px;
    outline: 0;
    font: inherit;
    font-size: 13.5px;
    line-height: 1.4;
  }

  .send-button {
    width: 42px;
    height: 42px;
    border: 0;
    border-radius: 999px;
    background: var(--crm-green);
    color: #fff;
    display: grid;
    place-items: center;
    cursor: pointer;
  }

  .send-button:disabled {
    opacity: .45;
    cursor: not-allowed;
  }

  .button-spinner {
    width: 17px;
    height: 17px;
    border: 2px solid rgba(255,255,255,.45);
    border-top-color: #fff;
    border-radius: 999px;
    animation: spin .8s linear infinite;
  }

  .toast {
    position: fixed;
    right: 18px;
    bottom: 18px;
    z-index: 80;
    max-width: min(420px, calc(100vw - 36px));
    padding: 12px 14px;
    border-radius: 14px;
    box-shadow: var(--crm-shadow);
    background: #fff;
    border: 1px solid var(--crm-line);
    color: var(--crm-text);
    font-size: 13px;
    font-weight: 650;
  }

  .toast.success {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #166534;
  }

  .toast.error {
    border-color: #fecaca;
    background: #fef2f2;
    color: #991b1b;
  }

  .crm-skeleton-list {
    display: grid;
    gap: 8px;
    padding: 8px;
  }

  .crm-skeleton-contact {
    height: 72px;
    border-radius: 16px;
    background: #fff;
    border: 1px solid var(--crm-line-soft);
    padding: 10px;
    display: grid;
    grid-template-columns: 42px 1fr;
    gap: 10px;
    align-items: center;
  }

  .crm-skeleton-contact span,
  .crm-skeleton-contact strong,
  .crm-skeleton-contact small,
  .crm-skeleton-contact em,
  .chat-loading span {
    display: block;
    border-radius: 999px;
    background: linear-gradient(90deg, #edf1f5, #fff, #edf1f5);
    background-size: 220% 100%;
    animation: shimmer 1.1s ease-in-out infinite;
  }

  .crm-skeleton-contact > span {
    width: 42px;
    height: 42px;
  }

  .crm-skeleton-contact strong {
    width: 70%;
    height: 11px;
  }

  .crm-skeleton-contact small {
    width: 52%;
    height: 9px;
    margin-top: 10px;
  }

  .crm-skeleton-contact em {
    width: 38%;
    height: 8px;
    margin-top: 10px;
  }

  .chat-loading span {
    width: 42px;
    height: 42px;
  }

  @keyframes shimmer {
    0% { background-position: 220% 0; }
    100% { background-position: -220% 0; }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 1120px) {
    .crm-shell {
      grid-template-columns: 340px minmax(0, 1fr);
    }

    .chat-context {
      display: none;
    }
  }

  @media (max-width: 820px) {
    .crm-shell {
      display: block;
      position: relative;
    }

    .crm-sidebar {
      position: fixed;
      inset: 0;
      z-index: 30;
      width: 100%;
      transform: translateX(-105%);
      transition: transform .22s ease;
      border-right: 0;
    }

    .crm-sidebar.is-open {
      transform: translateX(0);
    }

    .crm-main {
      min-height: 100dvh;
      height: 100dvh;
    }

    .chat-header {
      grid-template-columns: auto auto minmax(0, 1fr) auto;
      min-height: 64px;
      padding: 10px 12px;
    }

    .mobile-back {
      width: 38px;
      height: 38px;
      border: 1px solid var(--crm-line);
      border-radius: 12px;
      background: #fff;
      display: grid;
      place-items: center;
      color: #475467;
    }

    .phone {
      display: none;
    }

    .chat-body {
      padding: 14px 12px;
    }

    .bubble {
      max-width: 84%;
    }

    .composer {
      padding: 9px 10px 12px;
    }

    .template-row {
      padding-bottom: 2px;
    }

    .template-row button {
      white-space: nowrap;
    }

    .toast {
      right: 10px;
      bottom: 10px;
    }
  }
  .unlinked-alert {
    margin: 0 18px 10px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid #fed7aa;
    background: #fff7ed;
    color: #9a3412;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 8px;
  }

  .unlinked-alert strong {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: #fb923c;
    color: #fff;
    font-size: 12px;
  }

  .unlinked-alert span {
    font-size: 11.5px;
    line-height: 1.35;
  }

  .link-os-button {
    border-color: #bbf7d0 !important;
    background: #f0fdf4 !important;
    color: #166534 !important;
  }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 120;
    background: rgba(15, 23, 42, .42);
    display: grid;
    place-items: center;
    padding: 18px;
    backdrop-filter: blur(6px);
  }

  .link-modal {
    width: min(620px, 100%);
    max-height: min(720px, calc(100dvh - 36px));
    overflow: hidden;
    background: #fff;
    border-radius: 22px;
    box-shadow: 0 28px 80px rgba(15, 23, 42, .25);
    border: 1px solid rgba(255, 255, 255, .5);
    display: grid;
    grid-template-rows: auto auto auto minmax(0, 1fr);
  }

  .link-modal-head {
    padding: 18px 18px 12px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    border-bottom: 1px solid var(--crm-line-soft);
  }

  .link-modal-head span {
    display: block;
    color: var(--crm-green);
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .07em;
  }

  .link-modal-head h3 {
    margin: 4px 0 4px;
    font-size: 18px;
    line-height: 1.15;
  }

  .link-modal-head p {
    margin: 0;
    color: var(--crm-muted);
    font-size: 12px;
    line-height: 1.4;
  }

  .link-modal-head button {
    width: 34px;
    height: 34px;
    border: 1px solid var(--crm-line);
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    font-size: 20px;
    color: #64748b;
  }

  .link-search {
    margin: 12px 14px 8px;
    height: 42px;
    border: 1px solid var(--crm-line);
    border-radius: 14px;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 0 12px;
    color: var(--crm-muted);
  }

  .link-search input {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    font-size: 13px;
  }

  .link-current {
    margin: 0 14px 10px;
    padding: 10px 12px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid var(--crm-line-soft);
    display: grid;
    gap: 2px;
  }

  .link-current strong {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--crm-soft);
  }

  .link-current span {
    font-size: 12px;
    color: var(--crm-text);
    word-break: break-all;
  }

  .link-results {
    min-height: 0;
    overflow-y: auto;
    padding: 0 14px 14px;
    display: grid;
    gap: 8px;
  }

  .link-result-card {
    width: 100%;
    border: 1px solid var(--crm-line-soft);
    background: #fff;
    border-radius: 16px;
    padding: 10px;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    text-align: left;
    cursor: pointer;
    transition: .16s ease;
  }

  .link-result-card:hover {
    background: #f8fafc;
    border-color: #dbeafe;
  }

  .link-result-card:disabled {
    opacity: .65;
    cursor: wait;
  }

  .link-result-card strong {
    display: block;
    font-size: 13px;
    color: var(--crm-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .link-result-card span {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: var(--crm-muted);
  }

  .link-result-card em {
    font-style: normal;
    color: var(--crm-green);
    font-size: 12px;
    font-weight: 800;
  }

  .link-empty {
    padding: 26px 12px;
    text-align: center;
    color: var(--crm-muted);
    font-size: 13px;
  }

`;
